import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import dayjs from 'dayjs';

import {
  AreaChart,
  Area,
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip
} from 'recharts';

import { Box, Typography } from '@mui/material';

const ORANGE = 'rgb(203, 100, 35)';

const formatYAxisMoney = (value) => {
  const numericValue = Number(value || 0);

  if (Math.abs(numericValue) >= 1000000000) {
    return `${(numericValue / 1000000000).toFixed(1)}B`;
  }

  if (Math.abs(numericValue) >= 1000000) {
    return `${(numericValue / 1000000).toFixed(1)}M`;
  }

  if (Math.abs(numericValue) >= 1000) {
    return `${(numericValue / 1000).toFixed(0)}K`;
  }

  return numericValue.toLocaleString('en-ZA');
};

const getNiceStep = (range) => {
  if (!range || range <= 0) {
    return 1;
  }

  // Aim for roughly 4-5 intervals.
  const roughStep = range / 4;

  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const normalized = roughStep / magnitude;

  if (normalized <= 1) {
    return magnitude;
  }

  if (normalized <= 2) {
    return 2 * magnitude;
  }

  if (normalized <= 5) {
    return 5 * magnitude;
  }

  return 10 * magnitude;
};

const HistoricDataChart = ({ data = [], currency = 'R' }) => {
  const chartData = useMemo(() => {
    if (!Array.isArray(data)) {
      return [];
    }

    return [...data]
      .sort((a, b) => new Date(a.asof) - new Date(b.asof))
      .map((item) => ({
        ...item,
        label: dayjs(item.asof).format('DD MMM'),
        value: Number(item.sumSys || 0)
      }));
  }, [data]);

  const formatMoney = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return '-';
    }

    return `${currency}${Number(value).toLocaleString('en-ZA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  /**
   * Dynamically calculate the Y-axis.
   *
   * Example:
   *
   * min = 295K
   * max = 317K
   *
   * -> 295K
   * -> 300K
   * -> 305K
   * -> 310K
   * -> 315K
   * -> 320K
   *
   * No zero, so there is no huge empty gap below the data.
   */
  const yAxisConfig = useMemo(() => {
    if (!chartData.length) {
      return {
        domain: [0, 1],
        ticks: [0, 1]
      };
    }

    const values = chartData
      .map((item) => Number(item.value))
      .filter((value) => Number.isFinite(value));

    if (!values.length) {
      return {
        domain: [0, 1],
        ticks: [0, 1]
      };
    }

    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);

    // If all values are identical, create a small dynamic range.
    if (minValue === maxValue) {
      const padding = Math.abs(minValue * 0.05) || 1;

      const step = getNiceStep(padding * 2);

      const lower = Math.floor((minValue - padding) / step) * step;
      const upper = Math.ceil((maxValue + padding) / step) * step;

      const ticks = [];

      for (let value = lower; value <= upper; value += step) {
        ticks.push(Number(value.toFixed(10)));
      }

      return {
        domain: [lower, upper],
        ticks
      };
    }

    const range = maxValue - minValue;

    // Get a clean, rounded interval.
    const step = getNiceStep(range);

    /*
     * Start at the nearest clean value BELOW the minimum.
     *
     * This prevents the chart from starting at 0 while still
     * giving the lowest value some breathing room.
     */
    let lower = Math.floor(minValue / step) * step;

    /*
     * If the clean lower value is too far below the actual
     * minimum, move it up one interval.
     *
     * Example:
     *
     * min = 295K
     * step = 5K
     *
     * lower = 295K
     *
     * Good.
     *
     * If:
     *
     * min = 302K
     * step = 50K
     *
     * lower = 300K
     *
     * Good.
     *
     * This prevents the huge 0 -> 300K gap.
     */
    if (minValue - lower > step * 0.75) {
      lower += step;
    }

    /*
     * Make sure the lower bound never exceeds the
     * actual minimum.
     */
    if (lower > minValue) {
      lower -= step;
    }

    /*
     * Top of chart:
     * always go beyond the highest value.
     */
    let upper = Math.ceil(maxValue / step) * step;

    if (upper <= maxValue) {
      upper += step;
    }

    /*
     * If the upper bound is exactly on the highest value,
     * add another step so the line isn't sitting against
     * the top of the chart.
     */
    if (upper === maxValue) {
      upper += step;
    }

    /*
     * Build the tick list.
     */
    const ticks = [];

    for (let value = lower; value <= upper; value += step) {
      ticks.push(Number(value.toFixed(10)));
    }

    /*
     * Safety checks.
     */
    if (!ticks.length || ticks[0] > minValue) {
      ticks.unshift(lower);
    }

    if (ticks[ticks.length - 1] < maxValue) {
      ticks.push(upper);
    }

    return {
      domain: [lower, upper],
      ticks
    };
  }, [chartData]);

  if (!chartData.length) {
    return (
      <Box width="100%" height={320} display="flex" alignItems="center" justifyContent="center">
        <Typography
          variant="caption"
          sx={{
            color: '#666'
          }}>
          No historic balance data available
        </Typography>
      </Box>
    );
  }

  const totalDays =
    chartData.length > 1 ? dayjs(chartData.at(-1).asof).diff(dayjs(chartData[0].asof), 'day') : 0;

  return (
    <Box width="100%">
      <ResponsiveContainer width="100%" height={240}>
        <AreaChart
          data={chartData}
          margin={{
            top: 8,
            right: 15,
            left: 15,
            bottom: 5
          }}>
          <defs>
            <linearGradient id="historic-balance-gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={ORANGE} stopOpacity={0.15} />

              <stop offset="100%" stopColor={ORANGE} stopOpacity={0.02} />
            </linearGradient>
          </defs>

          <CartesianGrid vertical={false} stroke="#F4F4F4" strokeDasharray="0" />

          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            interval={0}
            tickMargin={8}
            tick={{
              fill: '#666',
              fontSize: 11
            }}
            tickFormatter={(value, index) => {
              const date = dayjs(chartData[index]?.asof);

              if (totalDays <= 10) {
                return value;
              }

              if (totalDays <= 45) {
                return index % 7 === 0 ? value : '';
              }

              return date.date() === 1 ? date.format('MMM') : '';
            }}
          />

          <YAxis
            domain={yAxisConfig.domain}
            ticks={yAxisConfig.ticks}
            tickFormatter={formatYAxisMoney}
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            width={50}
            tick={{
              fill: '#666',
              fontSize: 11
            }}
          />

          <Tooltip
            cursor={{
              stroke: '#E5E7EB',
              strokeWidth: 1
            }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) {
                return null;
              }

              const point = payload[0].payload;

              return (
                <Box
                  sx={{
                    bgcolor: '#fff',
                    boxShadow: '0 8px 24px rgba(0,0,0,.12)',
                    p: 1.5,
                    border: '1px solid #ECECEC',
                    minWidth: 190
                  }}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: '#666',
                      mb: 1
                    }}>
                    {dayjs(point.asof).format('DD MMM YYYY')}
                  </Typography>

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: ORANGE
                        }}
                      />

                      <Typography sx={{ fontSize: 12 }}>Balance</Typography>
                    </Box>

                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 700,
                        ml: 2
                      }}>
                      {formatMoney(point.sumSys)}
                    </Typography>
                  </Box>
                </Box>
              );
            }}
          />

          <Area
            type="monotone"
            dataKey="value"
            name="Balance"
            stroke={ORANGE}
            strokeWidth={2}
            fill="url(#historic-balance-gradient)"
            fillOpacity={1}
            dot={{
              r: 1,
              fill: ORANGE,
              stroke: ORANGE,
              strokeWidth: 1
            }}
            activeDot={{
              r: 3,
              fill: ORANGE,
              stroke: '#fff',
              strokeWidth: 2
            }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
};

HistoricDataChart.propTypes = {
  data: PropTypes.array,
  currency: PropTypes.string
};

export default HistoricDataChart;
